import {
  ArrowRight,
  Barcode,
  Check,
  CreditCard,
  ExternalLink,
  QrCode,
  Receipt,
  RefreshCw,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  billing as billingApi,
  type Payment,
  type PaymentMethod,
  type Plan,
  type Subscription,
} from '../api/resources.js';
import {
  Banner,
  Card,
  Empty,
  ErrorState,
  Loading,
  Spinner,
  Stat,
  StatusPill,
} from '../components/ui.js';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/input.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog.js';
import { useToast } from '../components/toast.js';
import { cn } from '../lib/cn.js';
import {
  INVOICE_STATUS_LABEL,
  PAYMENT_METHOD_LABEL,
  PAYMENT_STATUS_LABEL,
  daysUntil,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatNumber,
} from '../lib/format.js';
import { useAction, useAsync } from '../lib/hooks.js';

/**
 * Plano e cobrança (PRD §128, §129, §133; docs/mega-panel.md, D1 e D4).
 *
 * A experiência é a do produto-mãe: escolhe o tier e quantas instâncias, escolhe como
 * pagar, paga na página do gateway e volta. A tela nunca pede um cartão — quem pede é o
 * gateway — e enquanto o pagamento não confirma ela diz exatamente em que pé está.
 */

const TIER_COPY: Record<string, { label: string; blurb: string }> = {
  start: {
    label: 'Start',
    blurb: 'Para começar a operar: envio, recebimento, webhooks e 30 dias de histórico.',
  },
  business: {
    label: 'Business',
    blurb: 'Volume maior, mais equipe e 90 dias de histórico. O mesmo engine, mais folga.',
  },
};

const METHODS: Array<{ code: PaymentMethod; label: string; hint: string; icon: JSX.Element }> = [
  { code: 'PIX', label: 'Pix', hint: 'Confirmação em segundos.', icon: <QrCode /> },
  { code: 'CARD', label: 'Cartão de crédito', hint: 'Renovação automática.', icon: <CreditCard /> },
  { code: 'BOLETO', label: 'Boleto', hint: 'Compensa em até 2 dias úteis.', icon: <Barcode /> },
];

const NEEDS_PAYMENT = new Set(['PAST_DUE', 'GRACE_PERIOD', 'RESTRICTED', 'SUSPENDED']);

export function BillingPage() {
  const toast = useToast();
  const subscription = useAsync(() => billingApi.subscription(), []);
  const plans = useAsync(() => billingApi.plans(), []);
  const invoices = useAsync(() => billingApi.invoices(), []);
  // Filtro por período: o cliente escolhe datas, o servidor recorta (inclusive até o fim do dia).
  const [range, setRange] = useState<{ from: string; to: string }>({ from: '', to: '' });
  const payments = useAsync(
    () => billingApi.payments({ from: range.from || undefined, to: range.to || undefined }),
    [range.from, range.to],
  );
  const [checkoutPlan, setCheckoutPlan] = useState<Plan | null>(null);
  const [resumePayment, setResumePayment] = useState<Payment | null>(null);

  const reloadAll = () => {
    subscription.reload();
    invoices.reload();
    payments.reload();
  };

  const change = useAction(async (planCode: string) => {
    await billingApi.changePlan(planCode);
    toast.success('Plano alterado', 'O novo valor vale a partir da próxima fatura.');
    subscription.reload();
  });

  // Desistir de uma contratação ainda não paga: a fatura vira VOID e a cobrança some do gateway.
  const cancel = useAction(async (invoiceId: string) => {
    if (!window.confirm('Cancelar esta contratação? A cobrança em aberto será anulada.')) return;
    await billingApi.cancelInvoice(invoiceId);
    toast.success(
      'Contratação cancelada',
      'Nada foi cobrado. Você pode contratar de novo quando quiser.',
    );
    reloadAll();
  });

  if (subscription.loading) return <Loading />;
  if (subscription.error) {
    return <ErrorState error={subscription.error} onRetry={subscription.reload} />;
  }

  const current = subscription.data ?? null;
  const status = current?.status ?? 'TRIALING';
  const isActive = status === 'ACTIVE';
  const trialDays = status === 'TRIALING' ? daysUntil(current?.trialEnd) : null;
  const pending = current?.pendingPayment ?? null;
  const catalogue = plans.data?.data ?? [];
  const tiers = groupByTier(catalogue);

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="m-0 text-xl font-semibold">Plano e cobrança</h2>
          <p className="m-0 text-sm text-muted-foreground">
            Você paga por instância, no tier que precisar. Sem contrato de fidelidade.
          </p>
        </div>
      </div>

      {pending && (
        <Banner tone="warn">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <strong>Há uma cobrança aguardando pagamento</strong> —{' '}
              {formatCurrency(pending.amountCents)} via {PAYMENT_METHOD_LABEL[pending.method]}
              {pending.dueAt ? `, vence em ${formatDate(pending.dueAt)}` : ''}.
            </div>
            <div className="flex items-center gap-2">
              {pending.invoiceId && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={cancel.pending}
                  onClick={() => void cancel.run(pending.invoiceId as string)}
                >
                  Cancelar
                </Button>
              )}
              <Button size="sm" onClick={() => setResumePayment(pending)}>
                Continuar pagamento <ArrowRight />
              </Button>
            </div>
          </div>
        </Banner>
      )}

      {!pending && NEEDS_PAYMENT.has(status) && (
        <Banner tone={current?.capabilities.canSendMessages ? 'warn' : 'danger'}>
          <div>
            <strong>Sua assinatura está {statusPhrase(status)}.</strong>{' '}
            {current?.capabilities.canSendMessages
              ? 'Regularize o pagamento para evitar a restrição do envio de mensagens.'
              : 'O envio de mensagens está bloqueado; seus dados continuam acessíveis e exportáveis. Contrate um plano abaixo para reativar.'}
          </div>
        </Banner>
      )}

      {!pending && status === 'TRIALING' && trialDays !== null && (
        <Banner tone={trialDays <= 3 ? 'warn' : 'info'}>
          <div>
            <strong>
              {trialDays > 0
                ? `Seu período de teste termina em ${trialDays} dia${trialDays === 1 ? '' : 's'}.`
                : 'Seu período de teste terminou.'}
            </strong>{' '}
            Contrate um plano para continuar enviando mensagens sem interrupção.
          </div>
        </Banner>
      )}

      {current && !current.checkoutAvailable && (
        <Banner tone="info">
          A contratação online ainda não está disponível neste ambiente.{' '}
          <Link to="/support">Fale com o suporte</Link> para contratar.
        </Banner>
      )}

      <div className="grid grid-4">
        <Stat
          label="Plano atual"
          value={current?.plan?.name ?? 'Nenhum'}
          hint={<StatusPill status={status} />}
        />
        <Stat
          label="Mensalidade"
          value={formatCurrency(current?.plan?.priceCents ?? 0)}
          hint="por mês"
        />
        <Stat
          label="Instâncias incluídas"
          value={current?.plan?.instanceCount ?? 1}
          hint="números de WhatsApp"
        />
        <Stat
          label={isActive ? 'Próxima cobrança' : 'Período atual até'}
          value={formatDate(current?.currentPeriodEnd)}
          hint={
            status === 'TRIALING' && current?.trialEnd
              ? `teste até ${formatDate(current.trialEnd)}`
              : undefined
          }
        />
      </div>

      <Card
        title={isActive ? 'Mudar de plano' : 'Contratar um plano'}
        description="Escolha o tier pelo volume que você precisa e a quantidade de instâncias."
      >
        {change.error != null && <ErrorState error={change.error} />}
        {plans.loading ? (
          <Loading />
        ) : (
          <div className="grid gap-5 lg:grid-cols-2">
            {tiers.map(([tier, offers]) => (
              <section key={tier} className="min-w-0">
                <div className="mb-3">
                  <div className="flex items-center gap-2">
                    <h3 className="m-0 text-base font-semibold">
                      {TIER_COPY[tier]?.label ?? tier}
                    </h3>
                    {tier === 'business' && <Badge tone="brand">Mais escolhido</Badge>}
                  </div>
                  <p className="m-0 text-sm text-muted-foreground">
                    {TIER_COPY[tier]?.blurb ?? ''}
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {offers.map((plan) => (
                    <OfferCard
                      key={plan.code}
                      plan={plan}
                      current={plan.code === current?.plan?.code && isActive}
                      mode={isActive ? 'change' : 'buy'}
                      disabled={
                        change.pending ||
                        (current !== null && !current.checkoutAvailable && !isActive)
                      }
                      onSelect={() =>
                        isActive ? void change.run(plan.code) : setCheckoutPlan(plan)
                      }
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </Card>

      <Card
        title="Pagamentos"
        bodyless
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              De
              <Input
                type="date"
                aria-label="Data inicial"
                className="h-8 w-36"
                value={range.from}
                max={range.to || undefined}
                onChange={(event) => setRange((r) => ({ ...r, from: event.target.value }))}
              />
            </label>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              Até
              <Input
                type="date"
                aria-label="Data final"
                className="h-8 w-36"
                value={range.to}
                min={range.from || undefined}
                onChange={(event) => setRange((r) => ({ ...r, to: event.target.value }))}
              />
            </label>
            {(range.from || range.to) && (
              <Button variant="ghost" size="sm" onClick={() => setRange({ from: '', to: '' })}>
                Limpar
              </Button>
            )}
          </div>
        }
      >
        {payments.loading && !payments.data ? (
          <Loading />
        ) : (payments.data?.data.length ?? 0) === 0 ? (
          <Empty
            title={
              range.from || range.to ? 'Nenhum pagamento no período' : 'Nenhum pagamento ainda'
            }
            icon={<Receipt />}
          >
            <p className="muted m-0">
              {range.from || range.to
                ? 'Amplie o período para ver outros pagamentos.'
                : 'Os pagamentos aparecem aqui assim que você contratar.'}
            </p>
          </Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Plano</th>
                  <th>Meio</th>
                  <th>Valor</th>
                  <th>Status</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {(payments.data?.data ?? []).map((payment) => (
                  <tr key={payment.id}>
                    <td className="subtle">{formatDateTime(payment.createdAt)}</td>
                    <td>
                      {catalogue.find((p) => p.code === payment.planCode)?.name ??
                        payment.planCode ??
                        '—'}
                    </td>
                    <td>{PAYMENT_METHOD_LABEL[payment.method] ?? payment.method}</td>
                    <td>{formatCurrency(payment.amountCents)}</td>
                    <td>
                      <StatusPill
                        status={payment.status}
                        label={PAYMENT_STATUS_LABEL[payment.status] ?? payment.status}
                      />
                    </td>
                    <td className="text-right">
                      {payment.status === 'PENDING' && payment.checkoutUrl && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setResumePayment(payment)}
                        >
                          Pagar <ExternalLink />
                        </Button>
                      )}
                      {payment.status === 'PENDING' && payment.invoiceId && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={cancel.pending}
                          onClick={() => void cancel.run(payment.invoiceId as string)}
                        >
                          Cancelar
                        </Button>
                      )}
                      {payment.receiptUrl && (
                        <Button variant="ghost" size="sm" asChild>
                          <a href={payment.receiptUrl} target="_blank" rel="noreferrer">
                            Comprovante <ExternalLink />
                          </a>
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Faturas" bodyless>
        {(invoices.data?.data.length ?? 0) === 0 ? (
          <Empty title="Nenhuma fatura emitida" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Período</th>
                  <th>Vencimento</th>
                  <th>Total</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(invoices.data?.data ?? []).map((invoice) => (
                  <tr key={invoice.number}>
                    <td className="mono">{invoice.number}</td>
                    <td className="subtle">
                      {formatDate(invoice.periodStart)} – {formatDate(invoice.periodEnd)}
                    </td>
                    <td className="subtle">{formatDate(invoice.dueAt)}</td>
                    <td>{formatCurrency(invoice.totalCents)}</td>
                    <td>
                      <StatusPill
                        status={invoice.status}
                        label={INVOICE_STATUS_LABEL[invoice.status] ?? invoice.status}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <CheckoutDialog
        plan={checkoutPlan}
        resume={resumePayment}
        methods={current?.paymentMethods ?? ['PIX', 'CARD', 'BOLETO']}
        onClose={() => {
          setCheckoutPlan(null);
          setResumePayment(null);
        }}
        onPaid={() => {
          toast.success('Pagamento confirmado', 'Sua assinatura está ativa.');
          setCheckoutPlan(null);
          setResumePayment(null);
          reloadAll();
        }}
      />
    </>
  );
}

function OfferCard({
  plan,
  current,
  mode,
  disabled,
  onSelect,
}: {
  plan: Plan;
  current: boolean;
  mode: 'buy' | 'change';
  disabled: boolean;
  onSelect: () => void;
}) {
  const features = [
    `${plan.instanceCount} ${plan.instanceCount === 1 ? 'instância' : 'instâncias'}`,
    `${formatNumber(plan.limits.message_outbound ?? 0)} mensagens/mês`,
    `${formatNumber(plan.limits.api_request ?? 0)} requisições/mês`,
    `${plan.limits.retention_days ?? 0} dias de histórico`,
    `${plan.limits.members ?? 0} membros · ${plan.limits.webhooks ?? 0} webhooks`,
  ];
  return (
    <article
      className={cn(
        'flex flex-col rounded-lg border bg-card p-4 shadow-card',
        current ? 'border-primary ring-1 ring-primary' : 'border-border',
      )}
      aria-label={plan.name}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-medium">
          {plan.instanceCount === 1 ? '1 instância' : `${plan.instanceCount} instâncias`}
        </div>
        {current && <Badge tone="brand">Plano atual</Badge>}
      </div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className="text-2xl font-semibold tracking-tight tabular-nums">
          {formatCurrency(plan.priceCents)}
        </span>
        <span className="text-xs text-muted-foreground">/mês</span>
      </div>
      <ul className="my-3 flex flex-col gap-1.5 p-0 text-sm text-muted-foreground">
        {features.map((feature) => (
          <li key={feature} className="flex items-center gap-2">
            <Check className="size-3.5 shrink-0 text-success" aria-hidden="true" />
            {feature}
          </li>
        ))}
      </ul>
      <Button
        className="mt-auto"
        variant={current ? 'outline' : 'default'}
        disabled={current || disabled}
        onClick={onSelect}
      >
        {current ? 'Plano atual' : mode === 'buy' ? `Contratar ${plan.name}` : 'Mudar para este'}
      </Button>
    </article>
  );
}

/**
 * Do plano ao pagamento em dois passos: escolhe o meio, abre a página do gateway numa
 * aba nova e acompanha daqui. Quem volta com o pagamento feito vê a confirmação sem
 * recarregar; quem fechou a aba consegue reabrir a mesma cobrança.
 */
function CheckoutDialog({
  plan,
  resume,
  methods,
  onClose,
  onPaid,
}: {
  plan: Plan | null;
  resume: Payment | null;
  methods: PaymentMethod[];
  onClose: () => void;
  onPaid: () => void;
}) {
  const open = plan !== null || resume !== null;
  const [method, setMethod] = useState<PaymentMethod>(methods[0] ?? 'PIX');
  const [payment, setPayment] = useState<Payment | null>(null);

  useEffect(() => {
    setPayment(resume);
    setMethod(methods[0] ?? 'PIX');
  }, [resume, plan, methods]);

  const start = useAction(async () => {
    if (!plan) return;
    const created = await billingApi.checkout(plan.code, method);
    setPayment(created);
    if (created.checkoutUrl) window.open(created.checkoutUrl, '_blank', 'noopener');
  });

  const check = useAction(async () => {
    if (!payment) return;
    const fresh = await billingApi.payment(payment.id);
    setPayment(fresh);
    if (fresh.status === 'SUCCEEDED') onPaid();
  });

  // Enquanto o cliente paga na outra aba, esta pergunta a cada poucos segundos.
  useEffect(() => {
    if (!open || !payment || payment.status !== 'PENDING') return;
    const timer = setInterval(() => void check.run(), 5_000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, payment?.id, payment?.status]);

  const available = useMemo(() => METHODS.filter((m) => methods.includes(m.code)), [methods]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        {!payment ? (
          <>
            <DialogHeader>
              <DialogTitle>Contratar {plan?.name}</DialogTitle>
              <DialogDescription>
                {formatCurrency(plan?.priceCents ?? 0)} por mês. Escolha como pagar; você conclui na
                página segura do nosso parceiro de pagamento.
              </DialogDescription>
            </DialogHeader>
            <div className="my-4 grid gap-2" role="radiogroup" aria-label="Meio de pagamento">
              {available.map((option) => (
                <button
                  key={option.code}
                  type="button"
                  role="radio"
                  aria-checked={method === option.code}
                  onClick={() => setMethod(option.code)}
                  className={cn(
                    'flex items-center gap-3 rounded-md border px-3 py-2.5 text-left transition-colors',
                    method === option.code
                      ? 'border-primary bg-primary-soft'
                      : 'border-border hover:bg-muted',
                  )}
                >
                  <span className="text-primary [&_svg]:size-5">{option.icon}</span>
                  <span className="flex-1">
                    <span className="block text-sm font-medium">{option.label}</span>
                    <span className="block text-xs text-muted-foreground">{option.hint}</span>
                  </span>
                  {method === option.code && <Check className="size-4 text-primary" />}
                </button>
              ))}
            </div>
            {start.error != null && <ErrorState error={start.error} />}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="button" disabled={start.pending} onClick={() => void start.run()}>
                {start.pending ? 'Gerando cobrança…' : 'Ir para o pagamento'} <ExternalLink />
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>
                {payment.status === 'SUCCEEDED'
                  ? 'Pagamento confirmado'
                  : payment.status === 'PENDING'
                    ? 'Aguardando pagamento'
                    : 'Pagamento não concluído'}
              </DialogTitle>
              <DialogDescription>
                {payment.status === 'PENDING'
                  ? `${formatCurrency(payment.amountCents)} via ${PAYMENT_METHOD_LABEL[payment.method] ?? payment.method}. A página de pagamento abriu em outra aba; assim que o pagamento for confirmado, esta tela atualiza sozinha.`
                  : payment.status === 'SUCCEEDED'
                    ? 'Sua assinatura está ativa.'
                    : 'A cobrança não foi paga. Você pode gerar uma nova.'}
              </DialogDescription>
            </DialogHeader>
            <div className="my-4 flex items-center gap-3 rounded-md border border-border bg-muted/50 px-3 py-3 text-sm">
              {payment.status === 'PENDING' ? (
                <Spinner />
              ) : (
                <Check className="size-4 text-success" />
              )}
              <span>
                {PAYMENT_STATUS_LABEL[payment.status] ?? payment.status}
                {payment.dueAt && payment.status === 'PENDING'
                  ? ` · vence em ${formatDate(payment.dueAt)}`
                  : ''}
              </span>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Fechar
              </Button>
              {payment.status === 'PENDING' && payment.checkoutUrl && (
                <Button type="button" variant="outline" asChild>
                  <a href={payment.checkoutUrl} target="_blank" rel="noreferrer">
                    Abrir página de pagamento <ExternalLink />
                  </a>
                </Button>
              )}
              {payment.status === 'PENDING' && (
                <Button type="button" disabled={check.pending} onClick={() => void check.run()}>
                  <RefreshCw className={check.pending ? 'animate-spin' : ''} /> Já paguei
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function groupByTier(plans: Plan[]): Array<[string, Plan[]]> {
  const order = ['start', 'business', 'enterprise'];
  const groups = new Map<string, Plan[]>();
  for (const plan of plans) {
    const list = groups.get(plan.tier) ?? [];
    list.push(plan);
    groups.set(plan.tier, list);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => order.indexOf(a) - order.indexOf(b))
    .map(([tier, list]) => [tier, list.sort((a, b) => a.instanceCount - b.instanceCount)]);
}

function statusPhrase(status: string): string {
  switch (status) {
    case 'PAST_DUE':
      return 'com pagamento pendente';
    case 'GRACE_PERIOD':
      return 'em período de carência';
    case 'RESTRICTED':
      return 'restrita por falta de pagamento';
    case 'SUSPENDED':
      return 'suspensa por falta de pagamento';
    default:
      return status.toLowerCase();
  }
}
